import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Trash2, Plus, Pencil, ImagePlus, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Stars, photoSrc } from "@/components/Reviews";

const BLANK = { name: "", location: "", trip: "", rating: 5, text: "", photo_url: "", approved: true };
const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";

export default function ReviewsAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load reviews"));
  useEffect(() => { load(); }, []);

  const setApproved = async (id, approved) => {
    await api.put(`/admin/reviews/${id}`, { approved });
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, approved } : x)));
    toast.success(approved ? "Review published" : "Review hidden");
  };

  const remove = async (id) => {
    await api.delete(`/admin/reviews/${id}`);
    setItems((arr) => arr.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const uploadPhoto = async (f) => {
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("photo", f);
    try {
      const r = await api.post("/admin/reviews/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, photo_url: r.data.photo_url }));
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, rating: Number(editing.rating) || 5 };
    try {
      if (payload.id) await api.put(`/admin/reviews/${payload.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const pending = items.filter((x) => !x.approved).length;
  const shown = filter === "all" ? items : filter === "pending" ? items.filter((x) => !x.approved) : items.filter((x) => x.approved);

  return (
    <div data-testid="reviews-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-serif text-2xl font-bold text-ink">Reviews ({items.length}) {pending > 0 && <span className="ml-2 text-xs font-sans font-semibold bg-terra text-sand px-2.5 py-1 rounded-full align-middle" data-testid="reviews-pending-count">{pending} awaiting approval</span>}</h2>
        <div className="flex gap-2">
          <div className="flex gap-2" data-testid="reviews-filter">
            {["pending", "published", "all"].map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`reviews-filter-${s}`}
                className={`text-xs font-semibold px-3.5 py-1.5 rounded-full capitalize transition-colors ${filter === s ? "bg-ink text-sand" : "bg-white border border-ink/15 text-ink/60 hover:text-ink"}`}>{s}</button>
            ))}
          </div>
          <button onClick={() => setEditing({ ...BLANK })} data-testid="review-add-button"
            className="flex items-center gap-2 bg-terra text-sand text-xs font-semibold px-4 py-1.5 rounded-full hover:bg-ink transition-colors"><Plus size={14} /> Add review</button>
        </div>
      </div>

      {editing && (
        <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={save}
          className="bg-white border border-terra/30 rounded-2xl p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid="review-admin-form">
          <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Traveller name" className={inputCls} data-testid="review-admin-name" />
          <input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="City" className={inputCls} data-testid="review-admin-location" />
          <input value={editing.trip} onChange={(e) => setEditing({ ...editing, trip: e.target.value })} placeholder="Trip (e.g. Konark ride)" className={inputCls} data-testid="review-admin-trip" />
          <select value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} className={inputCls} data-testid="review-admin-rating">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
          </select>
          <textarea rows={3} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} placeholder="Review text" className={`${inputCls} sm:col-span-2`} data-testid="review-admin-text" />
          <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="review-admin-photo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="review-admin-photo-button"
              className="flex items-center gap-2 text-sm border border-ink/15 px-4 py-2 rounded-full hover:bg-sand disabled:opacity-60"><ImagePlus size={15} /> {uploading ? "Uploading..." : "Upload photo"}</button>
            <input value={editing.photo_url} onChange={(e) => setEditing({ ...editing, photo_url: e.target.value })} placeholder="or paste image URL" className={`${inputCls} flex-1 min-w-[200px]`} data-testid="review-admin-photo-url" />
            {editing.photo_url && <img src={photoSrc(editing.photo_url)} alt="" className="w-14 h-14 rounded-xl object-cover" />}
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={!!editing.approved} onChange={(e) => setEditing({ ...editing, approved: e.target.checked })} className="accent-[#D95D39] w-4 h-4" data-testid="review-admin-approved" /> Published
            </label>
          </div>
          <div className="sm:col-span-2 flex gap-3">
            <button type="submit" data-testid="review-admin-save" className="bg-ink text-sand text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-terra transition-colors">{editing.id ? "Update" : "Create"}</button>
            <button type="button" onClick={() => setEditing(null)} data-testid="review-admin-cancel" className="flex items-center gap-1 text-sm text-ink/60 px-4 py-2.5 rounded-full border border-ink/15 hover:bg-sand"><X size={14} /> Cancel</button>
          </div>
        </motion.form>
      )}

      <div className="grid gap-3">
        {shown.map((r) => (
          <div key={r.id} className="bg-white border border-ink/10 rounded-2xl p-4 flex gap-4" data-testid={`review-admin-row-${r.id.slice(0, 8)}`}>
            {r.photo_url ? <img src={photoSrc(r.photo_url)} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" /> : <div className="w-20 h-20 rounded-xl bg-sand shrink-0 hidden sm:block" />}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-ink">{r.name}{r.location && <span className="text-ink/40 font-normal"> · {r.location}</span>}</p>
                <Stars n={r.rating} />
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.approved ? "bg-emerald-100 text-emerald-700" : "bg-gold/30 text-ink"}`} data-testid={`review-admin-state-${r.id.slice(0, 8)}`}>
                  {r.approved ? "Published" : "Pending"}
                </span>
              </div>
              {r.trip && <p className="text-[11px] text-terra mt-0.5">{r.trip}</p>}
              <p className="text-sm text-ink/70 mt-1.5 leading-relaxed">{r.text}</p>
              <p className="text-[11px] text-ink/40 mt-1.5 font-mono flex items-center gap-1"><Clock size={10} /> {new Date(r.created_at).toLocaleString("en-IN")}</p>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              {r.approved ? (
                <button onClick={() => setApproved(r.id, false)} data-testid={`review-admin-hide-${r.id.slice(0, 8)}`} className="p-2 rounded-full text-ink/60 hover:bg-sand" title="Hide"><X size={16} /></button>
              ) : (
                <button onClick={() => setApproved(r.id, true)} data-testid={`review-admin-approve-${r.id.slice(0, 8)}`} className="p-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700" title="Approve"><Check size={16} /></button>
              )}
              <button onClick={() => setEditing({ ...BLANK, ...r })} data-testid={`review-admin-edit-${r.id.slice(0, 8)}`} className="p-2 rounded-full text-ink/60 hover:bg-sand hover:text-terra"><Pencil size={15} /></button>
              <button onClick={() => remove(r.id)} data-testid={`review-admin-delete-${r.id.slice(0, 8)}`} className="p-2 rounded-full text-ink/60 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="text-sm text-ink/50 py-8 text-center">No {filter === "all" ? "" : filter} reviews.</p>}
      </div>
    </div>
  );
}
